import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CreditCard, Zap, Check, Shield, Star, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BuyCredits() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPackage, setProcessingPackage] = useState(null);
  const [email, setEmail] = useState('');

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/credit-packages`);
      setPackages(response.data.packages);
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Failed to load credit packages');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (packageId) => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setProcessingPackage(packageId);
    
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin,
        email: email
      });

      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initialize payment');
      setProcessingPackage(null);
    }
  };

  const getPackageStyle = (packageId) => {
    switch (packageId) {
      case 'enterprise':
        return 'border-orange-500 ring-2 ring-orange-200 bg-gradient-to-b from-orange-50 to-white';
      case 'business':
        return 'border-blue-400 ring-2 ring-blue-100';
      case 'starter':
        return 'border-emerald-400 ring-2 ring-emerald-100';
      case 'premium':
        return 'border-purple-400 ring-2 ring-purple-100';
      default:
        return 'border-slate-200';
    }
  };

  const getButtonStyle = (packageId) => {
    switch (packageId) {
      case 'enterprise':
        return 'bg-orange-500 hover:bg-orange-600 text-white';
      case 'business':
        return 'bg-blue-500 hover:bg-blue-600 text-white';
      case 'starter':
        return 'bg-emerald-500 hover:bg-emerald-600 text-white';
      case 'premium':
        return 'bg-purple-500 hover:bg-purple-600 text-white';
      default:
        return 'bg-slate-800 hover:bg-slate-900 text-white';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-bold">
              <span className="text-slate-800">local</span>
              <span className="text-orange-500">Tradies</span>
              <span className="text-slate-800">Online</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-6">
              <Link to="/browse" className="text-slate-600 hover:text-slate-900">Find Tradies</Link>
              <Link to="/join" className="text-slate-600 hover:text-slate-900">Join as Tradie</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center bg-orange-500/20 text-orange-300 px-4 py-2 rounded-full text-sm mb-6">
            <Zap className="w-4 h-4 mr-2" />
            Grow Your Business with Quality Leads
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Purchase Lead Credits
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Get direct access to customers looking for your services. Each credit unlocks one qualified lead with full contact details.
          </p>
        </div>
      </section>

      {/* Email Input */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-md mx-auto px-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Your Email Address
          </label>
          <Input
            type="email"
            placeholder="Enter your email for receipt"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full"
            data-testid="email-input"
          />
          <p className="text-xs text-slate-500 mt-2">
            We'll send your purchase confirmation and credits to this email
          </p>
        </div>
      </section>

      {/* Packages */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg) => (
              <Card 
                key={pkg.id} 
                className={`relative overflow-hidden transition-all hover:shadow-lg ${getPackageStyle(pkg.id)}`}
                data-testid={`package-${pkg.id}`}
              >
                {pkg.id === 'enterprise' && (
                  <div className="absolute top-0 right-0 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    MOST POPULAR
                  </div>
                )}
                {pkg.id === 'business' && (
                  <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    BEST VALUE
                  </div>
                )}
                
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl">{pkg.name}</CardTitle>
                  <CardDescription>{pkg.description}</CardDescription>
                </CardHeader>
                
                <CardContent>
                  <div className="mb-6">
                    <div className="flex items-baseline">
                      <span className="text-4xl font-bold text-slate-900">${pkg.price}</span>
                      <span className="text-slate-500 ml-2">AUD</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      ${pkg.price_per_lead.toFixed(2)} per lead
                    </p>
                  </div>
                  
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center text-sm">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      <span>{pkg.credits} lead credits</span>
                    </li>
                    <li className="flex items-center text-sm">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      <span>Full contact details</span>
                    </li>
                    <li className="flex items-center text-sm">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      <span>No expiry date</span>
                    </li>
                    <li className="flex items-center text-sm">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      <span>Instant delivery</span>
                    </li>
                  </ul>
                  
                  <Button
                    className={`w-full ${getButtonStyle(pkg.id)}`}
                    onClick={() => handlePurchase(pkg.id)}
                    disabled={processingPackage !== null}
                    data-testid={`buy-${pkg.id}`}
                  >
                    {processingPackage === pkg.id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Buy Now
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold mb-2">Secure Payments</h3>
              <p className="text-sm text-slate-600">Powered by Stripe with bank-level encryption</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold mb-2">Instant Credits</h3>
              <p className="text-sm text-slate-600">Credits added immediately after payment</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <Star className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="font-semibold mb-2">Quality Leads</h3>
              <p className="text-sm text-slate-600">Real customers actively seeking services</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 px-4 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2">How do lead credits work?</h3>
                <p className="text-slate-600 text-sm">Each credit allows you to unlock one lead's full contact details. When a customer requests a quote for your service area, you can use a credit to view their phone number and email.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2">Do credits expire?</h3>
                <p className="text-slate-600 text-sm">No, your credits never expire. Use them whenever you need new customers.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2">What payment methods do you accept?</h3>
                <p className="text-slate-600 text-sm">We accept all major credit and debit cards through our secure Stripe payment system.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-400">
            &copy; {new Date().getFullYear()} Local Tradies Online. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
